import { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { setStatusSchema } from '../../../../shared/validators/schemas/set-status.schema';
import { ReportRecordDto } from '../../dtos/report-record.dto';
import { ReportActionEnum } from '../../enums/report-action.enum';
import { ReportStatusEnum } from '../../enums/report-status.enum';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
	record: ReportRecordDto,
	action: ReportActionEnum,
	abortEarly: boolean = false,
): ValidationFunctionResult => {
	// Check if record is provided
	if (!record) throw new Error('record is required.');

	let validationResult: ValidationResult | undefined;

	const fixedStatuses = [ReportStatusEnum.ACTIVE, ReportStatusEnum.ARCHIVED];

	switch (action) {
		case ReportActionEnum.ACTIVATE:
			validationResult = setStatusSchema(
				ReportStatusEnum.ACTIVE,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		case ReportActionEnum.ARCHIVE:
			validationResult = setStatusSchema(
				ReportStatusEnum.ARCHIVED,
				fixedStatuses,
			).validate(record, {
				abortEarly,
			});
			break;

		default:
			throw new Error(`Unknown action: ${action}`);
	}

	// Check for validation errors
	if (validationResult?.error) {
		return {
			isValid: false,
			errors: validationResult.error.details?.map((e) => e.message),
		};
	}

	// Return valid result if no errors
	return { isValid: true };
};
